import { useEffect, useState } from "react";
import { Loader2, FileClock, Receipt, Wallet, BellRing, RefreshCw, AlertTriangle, User, Truck, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { rupiah, fmtDate, fmtDateTime } from "@/lib/format";
import { AgingBadge, InvStatus } from "@/components/finance/aging";

const EV = {
  create: { icon: Receipt, label: "Invoice dibuat" }, payment: { icon: Wallet, label: "Pembayaran" },
  reminder: { icon: BellRing, label: "Pengingat terkirim" }, reminder_skipped: { icon: AlertTriangle, label: "Pengingat dilewati" },
  reminder_failed: { icon: AlertTriangle, label: "Pengingat gagal" }, odoo_sync: { icon: RefreshCw, label: "Sinkron Odoo" },
};

function Detail({ inv }) {
  return (
    <div className="inv-detail-grid" data-testid="invoice-detail">
      <div><User size={14} /><span>Dibuat oleh</span><b data-testid="invoice-created-by">{inv.created_by_name}</b><small>{inv.created_by_email} · {inv.created_by_role}</small></div>
      <div><Truck size={14} /><span>Tanggal DO</span><b data-testid="invoice-do-date">{fmtDate(inv.first_do_date)}</b><small>{(inv.do_numbers || []).join(", ") || "—"}</small></div>
      <div><Receipt size={14} /><span>Tanggal penagihan</span><b data-testid="invoice-date">{fmtDate(inv.invoice_date)}</b><small>dibuat {fmtDateTime(inv.created_at)}</small></div>
      <div><CalendarClock size={14} /><span>Jatuh tempo</span><b>{fmtDate(inv.due_date)}</b><small>{inv.ar_term_name} · {inv.ar_term_days} hari</small></div>
      <div><FileClock size={14} /><span>AR Aging</span><b><AgingBadge inv={inv} /></b><small>umur invoice {inv.age_days} hari</small></div>
      <div><RefreshCw size={14} /><span>Odoo</span><b>{inv.odoo_synced ? inv.number : "Lokal"}</b><small>{inv.odoo_synced ? `${inv.odoo_state} · ${inv.odoo_payment_state}` : "Tidak tersinkron ke Odoo"}</small></div>
    </div>
  );
}

export function InvoiceHistory({ invoiceId, onClose }) {
  const [inv, setInv] = useState(null);
  useEffect(() => {
    api.get(`/finance/invoices/${invoiceId}`).then((r) => setInv(r.data.data)).catch((e) => toast.error(apiError(e)));
  }, [invoiceId]);

  return (
    <Modal open onClose={onClose} className="activity-dialog" title={inv ? `Riwayat ${inv.number}` : "Riwayat Invoice"}
      subtitle={inv ? `${inv.outlet} · ${inv.so_number}` : ""} testId="invoice-history-dialog">
      {!inv ? <div className="activity-loading"><Loader2 className="spin" /> Memuat…</div> : (
        <>
          <div className="inv-status-bar">
            <InvStatus status={inv.status} testId="invoice-history-status" />
            <span>Total <b>{rupiah(inv.amount_total)}</b></span><span>Dibayar <b>{rupiah(inv.amount_paid)}</b></span>
            <span>Sisa <b data-testid="invoice-history-residual">{rupiah(inv.amount_residual)}</b></span>
          </div>
          <Detail inv={inv} />
          <div className="mini-line-table" style={{ marginTop: 14 }}>
            <div className="mini-line-row head"><span>Produk</span><span>Qty</span><span>Harga</span><span>Subtotal</span></div>
            {inv.lines.map((l) => (
              <div className="mini-line-row" key={l.product_odoo_id}><strong>{l.product_name}</strong><span>{l.quantity} {l.uom || ""}</span><span>{rupiah(l.unit_price)}</span><span>{rupiah(l.subtotal)}</span></div>
            ))}
          </div>
          <div className="activity-layout">
            <section>
              <h4><FileClock size={16} /> Riwayat Status</h4>
              <div className="audit-list" data-testid="invoice-events">
                {inv.events.map((e) => {
                  const m = EV[e.action] || { icon: FileClock, label: e.action };
                  return (
                    <div className={`audit-item inv-ev ${e.action}`} key={e.id} data-testid={`invoice-event-${e.action}`}>
                      <i />
                      <strong>{m.label}{e.amount ? ` · ${rupiah(e.amount)}` : ""}{e.status ? ` → ${e.status}` : ""}</strong>
                      <span>{e.note}</span>
                      <small>{e.actor_name || "Sistem"}{e.actor_email ? ` (${e.actor_email})` : ""} · {fmtDateTime(e.created_at)}</small>
                    </div>
                  );
                })}
              </div>
            </section>
            <section>
              <h4><Wallet size={16} /> Pembayaran ({inv.payments.length})</h4>
              <div className="notes-list">
                {inv.payments.length === 0 ? <p className="fin-muted">Belum ada pembayaran.</p> : inv.payments.map((p) => (
                  <article key={p.id} data-testid={`invoice-payment-${p.id}`}>
                    <p><b>{rupiah(p.amount)}</b> · {p.kind === "penuh" ? "Pelunasan" : "Sebagian"} · {p.journal_name || "Manual"}{p.reference ? ` · ${p.reference}` : ""}</p>
                    <small>Tgl bayar {fmtDate(p.payment_date)} · diverifikasi {p.verified_by_name} · {fmtDateTime(p.created_at)}</small>
                  </article>
                ))}
              </div>
            </section>
          </div>
        </>
      )}
    </Modal>
  );
}
